import {useFormik} from 'formik';
import {useCookies} from 'react-cookie';
import {useNavigate} from 'react-router-dom';
import Button from '../../../../../components/button';
import DotsLoading from '../../../../../components/dotsLoading';
import MainFooter from '../../../../../components/mazraeKit/footers/footerMain';
import SpinnerLoading from '../../../../../components/SpinnerLoading';
import useGetListLoanRequest from '../../../../../services/hookService/getListLoanRequest/useGetListLoanRequest';
import useGetListLoanRule from '../../../../../services/hookService/getListLoanRule/useGetListLoanRule';
import usePatchDirectLoanRequest from '../../../../../services/hookService/patchDirectLoanRequest/usePatchDirectLoanRequest';
import usePostLoanRequestFile from '../../../../../services/hookService/postLoanRequestFile/usePostLoanRequestFile';
import {
  LoanRequestStatus,
  showCancelButtonByLoanStatus,
} from '../../../../myAccount/my-credits/consts/loanStatus';
import {receiveLoanSteps} from '../../../receiveLoan/receiveLoanSteps';
import MultiStepComponent from '../../MultiStepComponent';
import {receiveCreditSteps} from '../../receiveCreditSteps';
import {useMultiStepForm} from '../../useMultiStepForm';
import LoanRequestHeader from '../components/LoanRequestHeader';
import BankAccountNumberInput from './BankAccountNumberInput';
import {
  directLoanDocsValidationSchema,
  getDirectLoanDocsInitialValues,
} from './formDetails';
import FullNameInput from './FullNameInput';
import InvoiceField from './InvoiceField';
import LivingAddressInput from './LivingAddressInput';
import NationalCardField from './NationalCardField';
import PostalCodeInput from './PostalCodeInput';
import UserPhoneNumberInput from './UserPhoneNumberInput';

const DirectLoanDocsPage = () => {
  const navigate = useNavigate();
  const {stepNumber} = useMultiStepForm();

  // Get the list of loan requests
  const {data: listLoanRequest, isLoading: isLoadingListLoanRequest} =
    useGetListLoanRequest({is_finished: false});
  const loanItem = listLoanRequest as ILoanRequestItem | undefined;

  const {loanRule} = useGetListLoanRule(
    loanItem?.guid as string,
    Boolean(loanItem),
  );
  // TODO: change this to local storage
  const [cookies] = useCookies(['user']);

  const {mutateFile, isLoadingFile} = usePostLoanRequestFile();
  const {mutate: mutateDirectLoan, isLoading} = usePatchDirectLoanRequest();

  const formInitialValues = getDirectLoanDocsInitialValues(
    cookies?.user?.profile,
  );

  const form = useFormik({
    initialValues: formInitialValues,
    validationSchema: directLoanDocsValidationSchema,
    enableReinitialize: true,
    validateOnBlur: true,
    onSubmit: values => {
      if (!loanItem || !loanRule) return;

      const commonFilePayload = {
        loan_request: loanItem.guid,
        loan_rule: loanRule.loan_request_rule.guid,
      };

      const uploadNationalCard = () => {
        const payload: IPostLoanRequestFilePayload = {
          ...commonFilePayload,
          loan_document: loanRule?.loan_request_rule?.loan_documents.find(
            doc => doc.title === 'id_card_image',
          )?.guid as string,
          file: values.nationalCardFile as File,
        };

        mutateFile(payload, {
          onSuccess: uploadInvoiceFile,
        });
      };

      const uploadInvoiceFile = () => {
        const payload: IPostLoanRequestFilePayload = {
          ...commonFilePayload,
          loan_document: loanRule?.loan_request_rule?.loan_documents.find(
            doc => doc.title === 'invoice_image',
          )?.guid as string,
          file: values.invoiceFile as File,
        };

        mutateFile(payload, {
          onSuccess: uploadSecondInvoiceFile,
        });
      };

      const uploadSecondInvoiceFile = () => {
        const payload: IPostLoanRequestFilePayload = {
          ...commonFilePayload,
          loan_document: loanRule?.loan_request_rule?.loan_documents.find(
            doc => doc.title === 'invoice_image_second',
          )?.guid as string,
          file: values.secondInvoiceFile as File,
        };

        mutateFile(payload, {
          onSuccess: submitForm,
        });
      };

      const submitForm = () => {
        const payload: IPatchDirectLoanRequestPayload = {
          loan_request_guid: loanItem.guid,
          name: values.ownerFullName,
          bank_account_number: values.bankAccountNumber,
          address: values.livingAddress,
          zip_code: values.postalCode,
          status: 10,
        };

        mutateDirectLoan(payload, {
          onSuccess: () => navigate('/receive-loan/step-4'),
        });
      };

      // Start the submission process
      uploadNationalCard();
    },
  });

  return (
    <div className="flex flex-col w-full bg-white h-dvh">
      {/* ---------------- HEADER ---------------- */}
      <LoanRequestHeader
        backPath="/my-credits"
        title="تکمیل مدارک"
        loanRequestGuid={listLoanRequest?.guid}
        showCancelButton={showCancelButtonByLoanStatus(
          listLoanRequest?.status as LoanRequestStatus,
        )}
      />

      {/* ---------------- CONTENT ---------------- */}

      <div className="flex overflow-y-auto flex-col gap-3 p-3 w-full h-full">
        {isLoadingListLoanRequest && (
          <div className="grid place-content-center h-full">
            <SpinnerLoading />
          </div>
        )}

        {loanItem && (
          <>
            <MultiStepComponent
              steps={
                loanItem?.is_direct ? receiveLoanSteps : receiveCreditSteps
              }
              currentStep={stepNumber}
            />

            <form onSubmit={form.handleSubmit}>
              <UserPhoneNumberInput form={form} />
              <FullNameInput form={form} />
              <BankAccountNumberInput form={form} />
              <NationalCardField form={form} />
              <InvoiceField form={form} />
              <LivingAddressInput form={form} />
              <PostalCodeInput form={form} />
            </form>
          </>
        )}
      </div>

      {/* ---------------- FOOTER ---------------- */}
      <div className="w-full flex flex-col gap-2  bg-white shadow-[0_-1px_6px_0_rgba(0,0,0,0.1)]">
        <div className="p-3">
          <Button
            onClick={() => form.submitForm()}
            className="w-full"
            disabled={isLoadingFile || isLoading}
          >
            {isLoadingFile || isLoading ? <DotsLoading /> : 'تایید'}
          </Button>
        </div>

        <MainFooter isScrolling={false} />
      </div>
    </div>
  );
};

export default DirectLoanDocsPage;
